import * as React from 'react';
import {
  Callout,
  DirectionalHint,
  FocusZone,
  FocusZoneDirection,
  IFocusZone,
  IList,
  ITextField,
  KeyCodes,
  Label,
  List,
  TextField,
  mergeStyleSets
} from '@fluentui/react';
import { MenuViewItem, MenuViewCloseSource, MenuViewStrings } from '@fluidx/office-fluid-types';
import { MenuButton } from './MenuButton';
import {
  menuCalloutStyles,
  menuContainerStyles,
  menuContainerFlipStyles,
  searchQueryStyle,
  searchResultsContainerStyles,
  searchResultsContainerFlipStyles,
  searchResultsNoResultsLoadingLabelStyles
} from './Menu.styles';
import { menuDataAutomationType } from '../testConstants';

export type MenuProps = Readonly<{
  /**
   * If true, the textbox will show up in the bottom
   */
  flip: boolean;

  /**
   * HTMLElement to attach Menu to
   */
  target: HTMLElement;

  /**
   * If false, the search menu will be hidden. Default behavior is we show the search menu.
   */
  showSearch?: boolean;

  /**
   * Css properties to render in the callout, used for theming amongst other things.
   */
  cssProperties?: () => { [propertyName: string]: string } | undefined;

  /**
   * String table that has the translated text of the strings used by the component.
   */
  stringTable: MenuViewStrings;

  /**
   * Flag that indicates when Menu needs to request focus from parent component to the menu.
   */
  shouldRequestFocus?: boolean;

  /**
   * An optional handler to perform some action when Discover closes.
   */
  onClose: (source: MenuViewCloseSource) => void;

  /**
   * The search results to be displayed in the list of components.
   */
  searchResults?: MenuViewItem[];

  /**
   * The current search query input into the search box.
   */
  searchQuery: string;

  /**
   * Callback used to update the search query value
   */
  setSearchQuery: (searchQuery: string) => void;

  /**
   * Optional callback used to restore focus to the host component and allow it to change the search query
   */
  restoreFocusAndUpdateQuery?: (textToInsert: string) => void;

  /**
   * Optional callback to let the host component determine whether Discover should run the dismiss function or not
   */
  shouldPreventDismiss?: (continueEditing: boolean) => boolean;

  /** LocaleDirection, the same as CSS direction values */
  direction?: string;
}>;

/**
 * This component displays a menu with a list of items. This menu has search
 * callback functionality where it may display a search box and request that the
 * menu items be updated in response to the query.
 */
export const Menu = React.memo(function Menu(props: MenuProps) {
  const { stringTable, onClose, searchResults, searchQuery, setSearchQuery, shouldPreventDismiss } = props;
  /**
   * A reference to the input element in the SearchBox.
   */
  const inputRef = React.createRef<ITextField>();

  /**
   * A reference to the Callout
   */
  const focusZoneRef = React.createRef<IFocusZone>();

  /**
   * A reference to the List component in the callout
   */
  const listRef = React.useRef<IList>(null);

  const listItemToFocusRef = React.useRef<HTMLButtonElement | null>(null);

  const [focusItemIndex, setFocusItemIndex] = React.useState<number | undefined>(undefined);

  /**
   * if true, the initial focus has not been set yet
   * Callout prevents inputRef to be updated on component load. This is required such that we only put focus on the input Textfield once
   */
  const hasInitializeInputFocusBeenSetRef = React.useRef<boolean>(false);

  const continueEditing = React.useRef<boolean>(false);

  // Everything that's an ascii alpha character, whitespace, or is a unicode character between 0x00 and 0x7F
  const validQueryCharacterRegex = /[\w ]|[^\x00-\x7F]/;

  /**
   * If true, focus on the first element in the Menu
   * Sets focus on the first item in the menu only when user goes from typing the query to hitting Down-arrow key
   * Prevents the focused item from being the first item every time user hits Down-arrow key
   */
  const hasArrowedIntoMenu = React.useRef<boolean>(false);

  /**
   * List of all supported actions for editing after arrowing into Discover.
   */
  const supportedKeyEventsOnPostArrowEdit: number[] = [KeyCodes.tab, KeyCodes.backspace];

  /**
   * Used to put focus in the input box on mount.
   */
  React.useEffect(() => {
    if (!hasInitializeInputFocusBeenSetRef.current && inputRef.current) {
      inputRef.current.focus();
      hasInitializeInputFocusBeenSetRef.current = true;
    }
  }, [inputRef]);

  React.useEffect(() => {
    if (
      props.shouldRequestFocus &&
      focusZoneRef.current &&
      listItemToFocusRef.current !== null &&
      !hasArrowedIntoMenu.current &&
      searchResults &&
      searchResults.length > 1
    ) {
      listItemToFocusRef.current.focus();
      listItemToFocusRef.current = null;
      hasArrowedIntoMenu.current = true;
    } else if (props.shouldRequestFocus && focusZoneRef.current && !hasArrowedIntoMenu.current) {
      focusZoneRef.current.focus(true);
    }
    // TODO: review for possible missing hook deps or document rationale for keeping eslint disable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusZoneRef]);

  /**
   * Captures onKeyDown from the input box, used to capture up arrow/down arrow/enter keys
   * for selection navigation.
   */
  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    switch (event.keyCode) {
      case KeyCodes.enter: {
        if (searchResults && focusItemIndex !== undefined && focusItemIndex < searchResults.length) {
          // listItemToFocus.current = null;
          commitAndClose(searchResults[focusItemIndex]);
        }
        event.preventDefault();
        return;
      }
      case KeyCodes.backspace: {
        if (searchQuery === '') {
          // listItemToFocus.current = null;
          onClose('searchBackspace');
          event.preventDefault();
        }
        return;
      }
      case KeyCodes.escape: {
        // listItemToFocus.current = null;
        onClose('searchEscape');
        event.stopPropagation();
        event.preventDefault();
        return;
      }
    }
  };

  /**
   * Invoked whenever the search query is changed in the input box.
   */
  const onSearchQueryChange = (_: any, newSearchQuery?: string) => {
    setSearchQuery(newSearchQuery ? newSearchQuery : '');
  };

  /**
   * Commits the given suggestion.
   */
  const commitAndClose = (data: MenuViewItem) => {
    // listItemToFocus.current = null;
    data.execute();
    onClose('executeItem');
  };

  const shouldShowSearchBox = (): boolean => {
    return props.showSearch !== undefined ? props.showSearch : true;
  };

  const shouldUpdateQueryOnFocusZoneKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): boolean => {
    /**
     * Check if user typed a word character or unicode characters for other languages or if the user key down action
     * was one of the key events we support for this scenario.
     */
    return (
      (event.key.match(validQueryCharacterRegex) && event.key.length === 1) ||
      supportedKeyEventsOnPostArrowEdit.includes(event.keyCode)
    );
  };

  const onFocusZoneKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    let actionHandled = false;

    if (event.keyCode === KeyCodes.escape) {
      // listItemToFocus.current = null;
      onClose('calloutEscape');
      actionHandled = true;
    } else if (
      shouldUpdateQueryOnFocusZoneKeyDown(event) &&
      props.restoreFocusAndUpdateQuery !== undefined &&
      !shouldShowSearchBox()
    ) {
      continueEditing.current = true;
      props.restoreFocusAndUpdateQuery(event.key);
      actionHandled = true;
    }

    if (actionHandled) {
      hasArrowedIntoMenu.current = false;
      event.preventDefault();
    }
  };

  const setItemToFocus = (menuItem: HTMLButtonElement | null) => {
    listItemToFocusRef.current = menuItem;
  };

  const renderResultItem = (item?: MenuViewItem, index?: number, _isScrolling?: boolean) => {
    return (
      item && (
        <MenuButton
          iconProps={{ iconName: item.icon }}
          // eslint-disable-next-line react/jsx-no-bind
          onClick={() => commitAndClose(item)}
          // eslint-disable-next-line react/jsx-no-bind
          onFocus={() => {
            if (index) {
              setFocusItemIndex(index);
            }
          }}
          text={item.displayName}
          // If the user didn't arrow into the menu, add an outline to the first item
          toggled={index === 0 && !props.shouldRequestFocus && !shouldShowSearchBox()}
          // eslint-disable-next-line react/jsx-no-bind
          refCallback={setItemToFocus}
          tabIndex={0}
          index={index}
        />
      )
    );
  };

  const styles =
    props.direction === undefined
      ? menuCalloutStyles
      : mergeStyleSets(menuCalloutStyles, { root: { direction: props.direction } });

  const directionalHint =
    props.direction === 'rtl'
      ? props.flip
        ? DirectionalHint.bottomRightEdge
        : DirectionalHint.topRightEdge
      : props.flip
      ? DirectionalHint.bottomLeftEdge
      : DirectionalHint.topLeftEdge;

  const discoverListId = 'discoverMenuDiv';

  return (
    <Callout
      data-automation-type={menuDataAutomationType}
      isBeakVisible={false}
      target={props.target}
      directionalHint={directionalHint}
      coverTarget={true}
      setInitialFocus={shouldShowSearchBox()}
      shouldRestoreFocus={false}
      styles={styles}
      style={props.cssProperties ? props.cssProperties() : undefined}
      // eslint-disable-next-line react/jsx-no-bind
      preventDismissOnEvent={() => {
        return shouldPreventDismiss ? shouldPreventDismiss(continueEditing.current) : false;
      }}
      // eslint-disable-next-line react/jsx-no-bind
      onDismiss={() => {
        continueEditing.current = false;
        // listItemToFocus.current = null;
        onClose('calloutDismiss');
      }}
    >
      <FocusZone
        componentRef={focusZoneRef}
        direction={FocusZoneDirection.vertical}
        // eslint-disable-next-line react/jsx-no-bind
        onKeyDown={onFocusZoneKeyDown}
      >
        <div
          style={props.flip ? menuContainerFlipStyles : menuContainerStyles}
          aria-expanded={searchResults && searchResults.length > 0 ? 'true' : 'false'}
        >
          {shouldShowSearchBox() && (
            <div style={{ display: 'flex' }}>
              <TextField
                defaultValue={searchQuery}
                aria-autocomplete={'list'}
                ariaLabel={stringTable.searchBoxAriaLabel}
                autoComplete="off"
                borderless={true}
                componentRef={inputRef}
                id="discover-textfield"
                // eslint-disable-next-line react/jsx-no-bind
                onChange={onSearchQueryChange}
                // eslint-disable-next-line react/jsx-no-bind
                onKeyDown={onKeyDown}
                placeholder={stringTable.search}
                styles={searchQueryStyle}
              />
            </div>
          )}

          <div
            className={props.flip ? searchResultsContainerFlipStyles : searchResultsContainerStyles}
            data-is-scrollable={true}
            aria-label={stringTable.listAriaLabel}
            id={discoverListId}
            data-automation-id={'discoverListTestId'}
          >
            {searchResults && searchResults.length > 0 && (
              <List
                aria-labelledby={discoverListId}
                role={'listbox'}
                items={searchResults}
                // eslint-disable-next-line react/jsx-no-bind
                onRenderCell={renderResultItem}
                componentRef={listRef}
              />
            )}
            {!searchResults && (
              <Label className={searchResultsNoResultsLoadingLabelStyles}>{stringTable.loading}</Label>
            )}
            {searchResults && searchResults.length === 0 && (
              <Label className={searchResultsNoResultsLoadingLabelStyles}>{stringTable.noResult}</Label>
            )}
          </div>
        </div>
      </FocusZone>
    </Callout>
  );
});
